import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import protoManager from './proto-manager.js';
import { IS_DEV } from './autogen/version.js';

import { getEnumLanguageName } from './autogen/bundle.js';

import { getEnumPlatformName                 } from './autogen/bundle.js';
import { getEnumPlatformValues               } from './autogen/bundle.js';

import { getEnumM3SeqABTestAudienceTagName   } from './autogen/bundle.js';
import { getEnumM3SeqABTestAudienceTagValues } from './autogen/bundle.js';

//-----------------------------------------------------------------------------

/**
 * @typedef {string} TraceId
 */

/** @returns {TraceId} */
export function genTraceId() {
  return uuidv4();
}

export const TRACE_ID_HEADER_NAME = 'trace-id';

//-----------------------------------------------------------------------------

/**
 * @typedef {Object} PeriodObject
 * @property {number} begin
 * @property {number} end
 */


export class Period {
  /**
   * @param {number} begin
   * @param {number} end
   */
  constructor(begin, end) {
    this.begin = begin;
    this.end   = end;
  }

  getDuration() {
    return this.end - this.begin;
  }
}


/**
 * @param {number} begin
 * @param {number} end
 * @returns {[?Period, ?Error]}
 */
export function createPeriod(begin, end) {
  if(end < begin) {
    return [null, new Error('Can not create period. Begin must be before end.')];
  }

  return [new Period(begin, end), null];
}


/**
 * @param {number} begin
 * @param {number} end
 * @returns {[?Period, ?Error]}
 */
export function createRealPeriod(begin, end) {
  if(end < begin) {
    return [null, new Error('Can not create period. Begin must be before end.')];
  }
  if(end === begin) {
    return [null, new Error('Can not create period. Duration is zero.')];
  }

  return [new Period(begin, end), null];
}

/**
 * @param {Period} firstPeriod
 * @param {number} pauseSec
 * @param {number} repeatEnd
 * @returns {Period[]}
 */
export function getRepeatedPeriods(firstPeriod, pauseSec, repeatEnd) {
  const periods = [firstPeriod];
  const duration = firstPeriod.getDuration();

  let begin = firstPeriod.end + pauseSec;
  let end = begin + duration;

  while(end <= repeatEnd) {
    const period = new Period(begin, end);
    periods.push(period);

    begin = end + pauseSec;
    end = begin + duration;
  }

  return periods;
}

//-----------------------------------------------------------------------------

/**
 * @typedef { import("./proto-manager.js").ProtoId } ProtoId
 * 
 * @typedef { import("./auth.js").Role  } Role
 * @typedef { import("./auth.js").Actor } Actor
 */

/**
 * @param {ProtoId} protoId 
 * @returns {string}
 */
export function getProtoAlias(protoId) {
  return protoManager.getAlias(protoId);
}

/**
 * @param {ProtoId} protoId
 * @returns {string}
 */
export function getHumanProtoAlias(protoId) {
  return protoManager.getAliasOrNone(protoId);
}

/**
 * @param {?string} protoAlias 
 * @returns {?ProtoId}
 */
export function getProtoId(protoAlias) {
  return protoManager.getId(protoAlias);
}

/**
 * @param {number} aProtoId
 * @param {number} bProtoId
 * @returns {number}
 */
export function compareProtoIdsByAlias(aProtoId, bProtoId) {
  if(aProtoId === bProtoId) {
    return 0;
  }

  const aAlias = protoManager.getAlias(aProtoId);
  const bAlias = protoManager.getAlias(bProtoId);
  if(aAlias < bAlias) {
    return -1;
  }
  if(aAlias > bAlias) {
    return 1;
  }
  return 0;
}

//-----------------------------------------------------------------------------

// NOTE: see EnumPlatform
export const PLATFORM_NONE    = 0;
export const PLATFORM_IOS     = 1;
export const PLATFORM_ANDROID = 2;
export const PLATFORM_AMAZON  = 3;
export const PLATFORM_SAMSUNG = 4;
export const PLATFORM_HUAWEI  = 5;

export function getPlatforms() {
  const platforms = [];

  for(const p of getEnumPlatformValues()) {
    if(p !== PLATFORM_NONE) {
      platforms.push(p);
    }
  }

  return platforms;
}

/**
 * @param {number} platform
 * @returns {string}
 */
export function getPlatformName(platform) {
  // formatted name
  switch(platform) {
    case PLATFORM_IOS:     return 'iOS';
    case PLATFORM_ANDROID: return 'Android';
    case PLATFORM_AMAZON:  return 'Amazon';
    case PLATFORM_SAMSUNG: return 'Samsung';
    case PLATFORM_HUAWEI:  return 'Huawei';
  }

  // name in upper case (not formatted)
  return getEnumPlatformName(platform);
}

/**
 * @param {number[]} platformsIds
 * @returns {string}
 */
export function convertPlatfromsIdsToStr(platformsIds) {
  let names = [];
  for(const platformId of platformsIds) {
    const name = getPlatformName(platformId);
    names.push(name);
  }
  return names.join(', ');
}

//-----------------------------------------------------------------------------

/**
 * @param {number} n
 * @returns {string}
 */
export function getSignedNumberStr(n) {
  const signStr = (n < 0) ? '-' : '+';

  const abs = Math.abs(n);
  const absStr = abs.toString();

  return signStr + absStr;
}

/**
 * @param {?number} n
 * @returns {boolean}
 */
export function isNumberSpecified(n) {
  if(n === undefined) {
    return false;
  }
  if(n === null) {
    return false;
  }
  if(n === 0) {
    return false;
  }
  return true;
}

/**
 * @param {?string} s 
 * @returns {boolean}
 */
export function isStringSpecified(s) {
  if(s === undefined) {
    return false;
  }
  if(s === null) {
    return false;
  }
  if(s.length === 0) {
    return false;
  }
  return true;
}

/**
 * @param {?number} n
 * @param {string} defaultStr
 * @returns {string}
 */
export function getOptionalNumberStr(n, defaultStr) {
  if(n === undefined) {
    return defaultStr;
  }
  if(n === null) {
    return defaultStr;
  }
  if(n === 0) {
    return defaultStr;
  }
  return n.toString();
}

/**
 * @param {number} stamp
 * @returns {string}
 */
export function getMomentStr(stamp) {
  if(stamp == 0) {
    return '---';
  }

  //const TEMPLATE = 'YYYY.MM.DD HH:mm:ss';
  const TEMPLATE = 'DD/MM/YYYY HH:mm:ss';
  return moment(stamp*1000).format(TEMPLATE);
}

/**
 * @param {?number} stamp
 * @returns {string}
 */
export function getOptionalMomentStr(stamp) {
  if(!isNumberSpecified(stamp)) {
    return 'Unknown';
  }
  return getMomentStr(stamp);
}

/**
 * @param {?number} stamp
 * @param {string} defaultStr
 * @returns {string}
 */
export function getOptionalMomentStrWithDefault(stamp, defaultStr) {
  if(!isNumberSpecified(stamp)) {
    return defaultStr;
  }
  return getMomentStr(stamp);
}

/**
 * @param {?number} stamp
 * @returns {string}
 */
export function getOptionalDashMomentStr(stamp) {
  const DASH = '—';
  return getOptionalTextMomentStr(stamp, DASH);
}

/**
 * @param {?number} stamp
 * @param {string} emptyCaseText
 * @returns {string}
 */
export function getOptionalTextMomentStr(stamp, emptyCaseText) {
  if(!isNumberSpecified(stamp)) {
    return emptyCaseText;
  }
  return getMomentStr(stamp);
}

/**
 * Время показано с точность до минут.
 * @param {number} stamp
 * @returns {string}
 */
export function getMomentMinStr(stamp) {
  if(stamp == 0) {
    return '---';
  }
  // Показывает момент во временной зоне UTC+3.
  const OFFSET = 3 * 60;

  const m = moment(stamp*1000).utcOffset(OFFSET);

  //const TEMPLATE = 'YYYY.MM.DD HH:mm';
  const TEMPLATE = 'DD/MM/YYYY HH:mm';
  return m.format(TEMPLATE);
}

/**
 * Время показано с точность до минут.
 * @param {?number} stamp
 * @param {string} defaultStr
 * @returns {string}
 */
export function getMomentMinStrOptionalWithDefault(stamp, defaultStr) {
  if(!isNumberSpecified(stamp)) {
    return defaultStr;
  }

  //const TEMPLATE = 'YYYY.MM.DD HH:mm';
  const TEMPLATE = 'DD/MM/YYYY HH:mm';
  return moment(stamp*1000).format(TEMPLATE);
}

/**
 * @param {number} durationSec
 * @returns {string}
 */
export function getDurationStrSimple(durationSec) {
  const MINUTE = 60;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  if(durationSec < 0) {
    return 'error';
  }
  if(durationSec < MINUTE) {
    const durationSecRounted = Math.floor(durationSec);
    return durationSecRounted + ' sec';
  }
  if(durationSec < HOUR) {
    const durationMin = Math.floor(durationSec / MINUTE);
    return durationMin + ' min';
  }
  if(durationSec < DAY) {
    const durationHour = Math.floor(durationSec / HOUR);
    return getAmountStr(durationHour, 'hour', 'hours');
  }

  const durationDay = Math.floor(durationSec / DAY);
  return getAmountStr(durationDay, 'day', 'days');
}

/**
 * @param {number} durationSec
 * @returns {string}
 */
export function getDurationStrSimpleRu(durationSec) {
  const MINUTE = 60;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  if(durationSec < 0) {
    return 'ошибка';
  }
  if(durationSec < MINUTE) {
    const durationSecRounted = Math.floor(durationSec);
    return durationSecRounted + ' сек';
  }
  if(durationSec < HOUR) {
    const durationMin = Math.floor(durationSec / MINUTE);
    return durationMin + ' мин';
  }
  if(durationSec < DAY) {
    const durationHour = Math.floor(durationSec / HOUR);
    return getAmountStr(durationHour, 'ч', 'ч');
  }

  const durationDay = Math.floor(durationSec / DAY);
  return getAmountStrRu(durationDay, 'день', 'дня', 'дней');
}

/**
 * @param {number} durationHours
 * @returns {string}
 */
export function convertDurationInHoursToStr(durationHours) {
  if(durationHours === 0) {
    return '0 hours';
  }

  const DAY = 24;

  const dayAmount = Math.floor(durationHours / DAY);
  const hourAmount = durationHours - dayAmount*DAY;

  const parts = [];
  if(dayAmount > 0) {
    parts.push(getAmountStr(dayAmount, 'day', 'days'));
  }
  if(hourAmount > 0) {
    parts.push(getAmountStr(hourAmount, 'hour', 'hours'));
  }
  return parts.join(' ');
}

/**
 * @param {Date} begin
 * @param {Date} end
 * @returns {string}
 */
export function getDurationStrBetweenMoments(begin, end) {
  const beginMSec = begin.getTime();
  const beginSec = Math.floor(beginMSec / 1000);

  const endMSec = end.getTime();
  const endSec = Math.floor(endMSec / 1000);

  const durationSec = endSec - beginSec;
  return convertDurationSecToStr(durationSec);
}

/**
 * @param {Date} moment
 * @param {number} durationSec
 * @returns {string}
 */
export function getDurationStrAfterMoment(moment, durationSec) {
  const momentMSec = moment.getTime();
  const momentSec = Math.floor(momentMSec / 1000);

  const endSec = momentSec + durationSec;
  const m = new Date(endSec * 1000);

  const yearStr  = convertUintTo2PosStr(m.getFullYear());
  const monthStr = convertUintTo2PosStr(m.getMonth()+1);
  const dateStr  = convertUintTo2PosStr(m.getDate());

  return dateStr+"."+monthStr+"."+yearStr + " " + convertStampToTimeStr(endSec);
}

/**
 * @param {number} durationSec
 * @returns {string}
 */
export function getDurationStr(durationSec) {
  return convertDurationSecToStr(durationSec);
}

/**
 * @param {number} durationSec
 * @returns {string}
 */
export function convertDurationSecToStr(durationSec) {
  if(durationSec < 0) {
    return '0 sec';
  }

  if(durationSec === 0) {
    return '0 sec';
  }

  const SEC_IN_DAY = 24 * 60 * 60;
  const SEC_IN_H   =      60 * 60;
  const SEC_IN_MIN =           60;

  const dayAmount = Math.floor(durationSec / SEC_IN_DAY);

  const dayRemainderSec = durationSec - dayAmount * SEC_IN_DAY;
  const hAmount = Math.floor(dayRemainderSec / SEC_IN_H);

  const hRemainderSec = dayRemainderSec - hAmount * SEC_IN_H;
  const minAmount = Math.floor(hRemainderSec / SEC_IN_MIN);

  const minRemainderSec = hRemainderSec - minAmount * SEC_IN_MIN;
  const secAmount = minRemainderSec;

  const parts = [];
  if(dayAmount > 0) {
    parts.push(getAmountStr(dayAmount, 'day', 'days'));
  }
  if(hAmount > 0) {
    parts.push(getAmountStr(hAmount, 'hour', 'hours'));
  }
  if(minAmount > 0) {
    parts.push(minAmount + ' min');
  }
  if(secAmount > 0) {
    parts.push(secAmount + ' sec');
  }
  return parts.join(' ');
}

/**
 * @param {number} durationSec
 * @returns {string}
 */
export function convertDurationSecToRuStr(durationSec) {
  if(durationSec < 0) {
    return '0 сек';
  }

  if(durationSec === 0) {
    return '0 сек';
  }

  const SEC_IN_DAY = 24 * 60 * 60;
  const SEC_IN_H   =      60 * 60;
  const SEC_IN_MIN =           60;

  const dayAmount = Math.floor(durationSec / SEC_IN_DAY);

  const dayRemainderSec = durationSec - dayAmount * SEC_IN_DAY;
  const hAmount = Math.floor(dayRemainderSec / SEC_IN_H);

  const hRemainderSec = dayRemainderSec - hAmount * SEC_IN_H;
  const minAmount = Math.floor(hRemainderSec / SEC_IN_MIN);

  const minRemainderSec = hRemainderSec - minAmount * SEC_IN_MIN;
  const secAmount = minRemainderSec;

  const parts = [];
  if(dayAmount > 0) {
    parts.push(getAmountStrRu(dayAmount, 'день', 'дня', 'дней'));
  }
  if(hAmount > 0) {
    parts.push(getAmountStrRu(hAmount, 'час', 'часа', 'часов'));
  }
  if(minAmount > 0) {
    parts.push(minAmount + ' мин');
  }
  if(secAmount > 0) {
    parts.push(secAmount + ' сек');
  }
  return parts.join(' ');
}

/**
 * If duration is between 1 day and 90 days, function returns duration in days.
 * It is requirement for billing pages (billing page of all players and billing page of one player).
 * 
 * @param {number} durationSec
 * @returns {string}
 */
export function getDurationStrForBilling(durationSec) {
  const MINUTE = 60;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;
  const MONTH = 30 * DAY;
  const YEAR = 12 * MONTH;

  if(durationSec < 0) {
    return 'error';
  }
  if(durationSec < MINUTE) {
    return durationSec + ' sec';
  }
  if(durationSec < HOUR) {
    const durationMin = Math.floor(durationSec / MINUTE);
    return durationMin + ' min';
  }
  if(durationSec < DAY) {
    const durationHour = Math.floor(durationSec / HOUR);
    return getAmountStr(durationHour, 'hour', 'hours');
  }
  if(durationSec < 90*DAY) {
    const durationDay = Math.floor(durationSec / DAY);
    return getAmountStr(durationDay, 'day', 'days');
  }
  if(durationSec < YEAR) {
    const durationMonth = Math.floor(durationSec / MONTH);
    return getAmountStr(durationMonth, 'month', 'months');
  }

  const durationYear = Math.floor(durationSec / YEAR);
  return getAmountStr(durationYear, 'year', 'years');
}

export function convertDurationSecToShortStr(durationSec) {
  if(durationSec < 0) {
    return 'invalid duration sec';
  }

  if(durationSec === 0) {
    return '00:00:00';
  }

  const SEC_IN_DAY = 24 * 60 * 60;
  const SEC_IN_H   =      60 * 60;
  const SEC_IN_MIN =           60;

  const dayAmount = Math.floor(durationSec / SEC_IN_DAY);

  const dayRemainderSec = durationSec - dayAmount * SEC_IN_DAY;
  const hAmount = Math.floor(dayRemainderSec / SEC_IN_H);

  const hRemainderSec = dayRemainderSec - hAmount * SEC_IN_H;
  const minAmount = Math.floor(hRemainderSec / SEC_IN_MIN);

  const minRemainderSec = hRemainderSec - minAmount * SEC_IN_MIN;
  const secAmount = minRemainderSec;

  const hStr   = convertUintTo2PosStr(hAmount);
  const minStr = convertUintTo2PosStr(minAmount);
  const secStr = convertUintTo2PosStr(secAmount);
  const timeStr = hStr + ':' + minStr + ':' + secStr;

  const parts = [];
  if(dayAmount > 0) {
    parts.push(getAmountStr(dayAmount, 'day', 'days'));
  }

  parts.push(timeStr);

  return parts.join(' ');
}

/**
 * @param {number} amount
 * @param {string} unitSingular
 * @param {string} unitPlural
 * @returns {string}
 */
export function getAmountStr(amount, unitSingular, unitPlural) {
  const unit = (amount === 1) ? unitSingular : unitPlural;
  return amount + ' ' + unit;
}

/**
 * @param {number} amount
 * @param {string} unitSingular
 * @param {string} unitPlural1
 * @param {string} unitPlural2
 * @returns {string}
 */
function getAmountStrRu(amount, unitSingular, unitPlural1, unitPlural2) {
  const unit = getAmountStrRuUnit(amount, unitSingular, unitPlural1, unitPlural2);
  return amount + ' ' + unit;
}

/**
 * @param {number} amount
 * @param {string} unitSingular
 * @param {string} unitPlural1
 * @param {string} unitPlural2
 * @returns {string}
 */
function getAmountStrRuUnit(amount, unitSingular, unitPlural1, unitPlural2) {
  if(amount >= 11 && amount <= 19) {
    return unitPlural2;
  }

  const r = amount % 10;
  if(r === 1) {
    return unitSingular;
  }
  if(r >= 2 && r <= 4) {
    return unitPlural1;
  }
  
  return unitPlural2;
}

/**
 * @param {number} delta
 * @returns {Object}
 */
export function getDeltaClass(delta) {
  return {
    increased: delta > 0,
    decreased: delta < 0,
  };
}

export const LOAD_STATE_INIT    = 0;
export const LOAD_STATE_LOADING = 1;
export const LOAD_STATE_LOADED  = 2;

/**
 * @param {any[]} array
 * @param {any} value
 */
export function arrayDeleteFirstItemByValue(array, value) {
  const index = array.indexOf(value);
  if(index !== -1) {
    array.splice(index, 1);
  }
}

export const ROLE_ADMIN     = 1;
export const ROLE_MODERATOR = 2;
export const ROLE_READER    = 3;

/**
 * @param {Role} role
 * @returns {string}
 */
export function getRoleName(role) {
  switch(role) {
    case ROLE_ADMIN:     return 'administrator';
    case ROLE_MODERATOR: return 'moderator';
    case ROLE_READER:    return 'reader';
  }
  return `Unknown role code ${role}.`;
}

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} firstName
 * @property {string} lastName
 */

/**
 * @param {?User} user
 * @returns {?string}
 */
export function getUserFullName(user) {
  if(user === null) {
    return null;
  }

  const hasFirstName = ('firstName' in user) && user.firstName.length > 0;
  const hasLastName  = ( 'lastName' in user) && user.lastName.length > 0;

  if(hasFirstName && hasLastName) {
    return user.firstName + ' ' + user.lastName;
  }
  if(hasFirstName && !hasLastName) {
    return user.firstName;
  }

  const hasId = ('id' in user);
  if(hasId) {
    return 'User ' + user.id;
  }

  return 'User';
}

//-----------------------------------------------------------------------------
// Permissions
//-----------------------------------------------------------------------------

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayAddUserByRole(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayReadUserActions(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayEditUserInfo(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Actor} actor
 * @param {number} userId
 * @returns {boolean}
 */
export function mayChangePassword(actor, userId) {
  if(actor.id === userId) {
    // Any user may change their own password.
    return true;
  }
  return isRoleAdmin(actor.role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManagePushNotifications(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayChangeClientMinVersion(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManageMaintenance(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManagePlayersNetSessionsLogging(role) {
  return isRoleAdmin(role);
}

//-----------------------------------------------------------------------------

/**
 * Return true, if user may do any action with tmnt.
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManageTmnts(role) {
  if(mayStartTmnt(role)) {
    return true;
  }
  if(mayFinalizeTmnt(role)) {
    return true;
  }
  if(mayDeleteTmnt(role)) {
    return true;
  }
  return false;
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayStartTmnt(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayFinalizeTmnt(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayDeleteTmnt(role) {
  return isRoleAdmin(role);
}

//-----------------------------------------------------------------------------

/**
 * Return true, if user may do any action with mesa.
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManageMesas(role) {
  if(mayStartMesa(role)) {
    return true;
  }
  if(mayFinalizeMesa(role)) {
    return true;
  }
  if(mayDeleteMesa(role)) {
    return true;
  }
  return false;
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayStartMesa(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayFinalizeMesa(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayDeleteMesa(role) {
  return isRoleAdmin(role);
}

//-----------------------------------------------------------------------------

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayManageGvnts(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayStartGvnt(role) {
  return isRoleAdmin(role);
}

//-----------------------------------------------------------------------------

/**
 * @param {Role} role 
 * @returns {boolean}
 */
export function mayManageQAUsers(role) {
  return isRoleModeratorOrAdmin(role);
}

// Use function mayEditPlayerData from file "model/player.js".
/**
 * @param {Role} role 
 * @returns {boolean}
 */
export function mayEditPlayerData(role) {
  return isRoleModeratorOrAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayBanPlayer(role) {
  return isRoleModeratorOrAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function mayUnbanPlayer(role) {
  return isRoleModeratorOrAdmin(role);
}

/**
 * @param {Role} role 
 * @returns {boolean}
 */
export function mayAddMassPending(role) {
  return isRoleAdmin(role);
}

//-----------------------------------------------------------------------------

/**
 * @param {Role} role
 * @returns {boolean}
 */
 export function mayManage(role) {
  return isRoleAdmin(role);
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function isRoleAdmin(role) {
  return role === ROLE_ADMIN;
}

/**
 * @param {Role} role
 * @returns {boolean}
 */
export function isRoleModeratorOrAdmin(role) {
  return role === ROLE_MODERATOR || role === ROLE_ADMIN;
}

//-----------------------------------------------------------------------------

/**
 * @param {string} str
 * @returns {string}
 */
export function getStrWithLowFirstLetter(str) {
  if(str === undefined) {
    return str;
  }
  if(str === null) {
    return str;
  }
  if(str.length === 0) {
    return str;
  }
  const firstLetter = str[0];
  return firstLetter.toLowerCase() + str.substring(1);
}

/**
 * @typedef {Object} Entity
 * @property {number} id
 */

/**
 * @param {Entity[]} objects 
 * @param {number} id 
 * @returns {?Entity}
 */
export function findObjectById(objects, id) {
  if(objects === null) {
    return null;
  }

  for(const object of objects) {
    if(object.id === id) {
      return object;
    }
  }
  return null;
}

//-----------------------------------------------------------------------------

/**
 * @param {Date} m
 * @returns {number}
 */
export function convertMomentToSec(m) {
  return convertMomentToStamp(m);
}

/**
 * @param {Date} moment
 * @returns {number}
 */
export function convertMomentToStamp(moment) {
  return Math.floor(moment.getTime() / 1000);
}

//-----------------------------------------------------------------------------

/**
 * @param {number} stamp
 * @returns {string}
 */
export function convertStampToDateStr(stamp) {
  const m = new Date(stamp * 1000);
  return convertMomentToDateStr(m);
}

/**
 * @param {number} stamp
 * @returns {string}
 */
export function convertStampToTimeStr(stamp) {
  const m = new Date(stamp * 1000);
  return convertMomentToTimeStr(m);
}
 
/**
 * Depricated. Error in function name. Must be 'ToDateStr'.
 * Use function convertMomentToDateStr.
 *
 * @param {Date} m
 * @returns {string}
 */
export function convertMomentToDataStr(m) {
  return convertMomentToDateStr(m);
}

/**
 * @param {Date} m 
 * @returns {string}
 */
export function convertMomentToDateStr(m) {
  const UTC3_OFFSET_MSEC = 3 * 60 * 60 * 1000;
  const shdifedDate = new Date(m.getTime() + UTC3_OFFSET_MSEC);

  const yearStr  = convertUintTo2PosStr(shdifedDate.getUTCFullYear());
  const monthStr = convertUintTo2PosStr(shdifedDate.getUTCMonth()+1);
  const dateStr  = convertUintTo2PosStr(shdifedDate.getUTCDate());
  return yearStr + '-' + monthStr + '-' + dateStr;
}

/**
 * @param {Date} m
 * @returns {string}
 */
export function convertMomentToTimeStr(m) {
  const UTC3_OFFSET_MSEC = 3 * 60 * 60 * 1000;
  const shdifedDate = new Date(m.getTime() + UTC3_OFFSET_MSEC);

  const hStr   = convertUintTo2PosStr(shdifedDate.getUTCHours());
  const minStr = convertUintTo2PosStr(shdifedDate.getUTCMinutes());
  const secStr = convertUintTo2PosStr(shdifedDate.getUTCSeconds());
  return hStr + ':' + minStr + ':' + secStr;
}

/**
 * @param {number} n
 * @returns {string}
 */
export function convertUintTo2PosStr(n) {
  if(n >= 10) {
    return n.toString();
  }
  return '0' + n.toString();
}

/**
 * @param {?string} dataStr
 * @param {?string} timeStr
 * @returns {?Date}
 */
export function convertDataStrAndTimeStrToLocalData(dataStr, timeStr) {
  if(dataStr === null) {
    // invalid input
    return null;
  }

  if(timeStr === null) {
    // invalid input
    return null;
  }

  const dateObj = parseDataStr(dataStr);
  if(dateObj === null) {
    // invalid format
    return null;
  }

  const timeObj = parseTimeStr(timeStr);
  if(timeObj === null) {
    // invalid format
    return null;
  }

  const monthIndex = dateObj.month - 1;
  return new Date(dateObj.year, monthIndex, dateObj.day, timeObj.h, timeObj.min, timeObj.sec);
}

/**
 * @param {?string} dataStr
 * @param {?string} timeStr
 * @returns {?Date}
 */
export function convertDataStrAndTimeStrToUTC3Data(dataStr, timeStr) {
  if(dataStr === null) {
    // invalid input
    return null;
  }

  if(timeStr === null) {
    // invalid input
    return null;
  }

  const dateObj = parseDataStr(dataStr);
  if(dateObj === null) {
    // invalid format
    return null;
  }

  const timeObj = parseTimeStr(timeStr);
  if(timeObj === null) {
    // invalid format
    return null;
  }

  const monthIndex = dateObj.month - 1;

  const msecUTC = Date.UTC(dateObj.year, monthIndex, dateObj.day, timeObj.h, timeObj.min, timeObj.sec);
  const msecShifted = msecUTC - 3 * 60 * 60 * 1000;
  return new Date(msecShifted);
}

/**
 * @typedef {Object} DateObj
 * @property {number} year
 * @property {number} month
 * @property {number} day
 */

/**
 * @param {?string} dataStr - Example: '2020-06-22'
 * @returns {?DateObj}
 */
function parseDataStr(dataStr) {
  if(dataStr === null) {
    // invalid input
    return null;
  }

  const parts = dataStr.split('-');
  if(parts.length !== 3) {
    // invalid format
    return null;
  }

  const year = parseInt(parts[0], 10);
  if(isNaN(year)) {
    // invalid format
    return null;
  }

  const month = parseInt(parts[1], 10);
  if(isNaN(month)) {
    // invalid format
    return null;
  }

  const day = parseInt(parts[2], 10);
  if(isNaN(day)) {
    // invalid format
    return null;
  }

  return {
    year: year,
    month: month,
    day: day,
  };
}

/**
 * @typedef {Object} TimeObj
 * @property {number} h
 * @property {number} min
 * @property {number} sec
 */

/**
 * @param {?string} timeStr - Examples: '18:50', '18:50:04'.
 * @returns {?TimeObj}
 */
function parseTimeStr(timeStr) {
  if(timeStr === null) {
    // invalid input
    return null;
  }

  let h = 0;
  let min = 0;
  let sec = 0;

  const parts = timeStr.split(':');

  if(parts.length > 0) {
    h = parseInt(parts[0], 10);
    if(isNaN(h)) {
      // invalid format
      return null;
    }
  }

  if(parts.length > 1) {
    min = parseInt(parts[1], 10);
    if(isNaN(min)) {
      // invalid format
      return null;
    }
  }

  if(parts.length > 2) {
    sec = parseInt(parts[2], 10);
    if(isNaN(sec)) {
      // invalid format
      return null;
    }
  }

  return {
    h,
    min,
    sec,
  };
}

export function getPeriodLast24Hours() {
  const now = new Date();

  const DURATION_HOURS = 24;
  const begin = new Date(now.getTime());
  begin.setHours(begin.getHours() - DURATION_HOURS);

  return {
    begin: begin,
    end: now,
  };
}

export const SKU_PREFIX = 'games.bit.storyngtonhall.';

/**
 * @param {string} sku
 * @returns {string}
 */
export function getShortSKU(sku) {
  if(sku.startsWith(SKU_PREFIX)) {
    return sku.substring(SKU_PREFIX.length);
  }
  return sku;
}

//-----------------------------------------------------------------------------

export const LANG_UNKNOWN = 2;

// See EnumLanguage in file meta/enum.meta
export const LANG_EN = 0; // английский
export const LANG_RU = 1; // русский
export const LANG_DE = 3; // немецкий
export const LANG_PT = 4; // португальский
export const LANG_ES = 5; // испанский
export const LANG_FR = 6; // французский
export const LANG_KO = 7; // корейский
export const LANG_JA = 8; // японский

export function getLangs() {
  return [
    LANG_EN,
    LANG_RU,
    LANG_DE,
    LANG_PT,
    LANG_ES,
    LANG_FR,
    LANG_KO,
    LANG_JA,
  ];
}

export function getOptionalLangOptions() {
  const options = getLangOptions();

  const nullOption = {
    value: LANG_UNKNOWN,
    text: 'any',
  };
  options.unshift(nullOption);

  return options;
}

export function getLangOptions() {
  const options = [];

  for(const lang of getLangs()) {
    const option = {
      value: lang,
      text: getLangNameEn(lang).toLowerCase(),
    };
    options.push(option);
  }

  return options;
}

/**
 * @param {number} lang
 * @returns {string}
 */
export function getLangStrCode(lang) {
  return getEnumLanguageName(lang);
}

/**
 * @param {number} lang Numeric code from EnumLanguage.
 * @return {string}
 */
export function getLangNameEn(lang) {
  switch(lang) {
    case LANG_UNKNOWN: return 'unknown';

    case LANG_EN: return 'English';
    case LANG_RU: return 'Russian';
    case LANG_DE: return 'German';
    case LANG_PT: return 'Portuguese';
    case LANG_ES: return 'Spanish';
    case LANG_FR: return 'French';
    case LANG_KO: return 'Korean';
    case LANG_JA: return 'Japanese';

    default:
      return `Unknown lang code (${lang})`;
  }
}

/**
 * @param {number} lang Numeric code from EnumLanguage.
 * @return {string}
 */
export function getLangNameRu(lang) {
  switch(lang) {
    case LANG_UNKNOWN: return 'unknown';

    case LANG_EN: return 'Английский';
    case LANG_RU: return 'Русский';
    case LANG_DE: return 'Немецкий';
    case LANG_PT: return 'Португальский';
    case LANG_ES: return 'Испанский';
    case LANG_FR: return 'Французский';
    case LANG_KO: return 'Корейский';
    case LANG_JA: return 'Японский';

    default:
      return `Unknown lang code (${lang})`;
  }
}

//-----------------------------------------------------------------------------

/**
 * Example
 * Number 16000200 -> String '16.2.0'
 * 
 * Converstion from version string to version code is in
 * server/gamed/game/version/version.go
 * func NewVersion
 * 
 * @param {number} code
 * @returns {string}
 */
export function convertVersionCodeToStr(code) {
  const patch = code % 100;

  const majorMinorCode = Math.floor(code / 100);
  const minor = majorMinorCode % 10000;

  const major = Math.floor(code / 1000000);

  const majorStr = major.toString();
  const minorStr = minor.toString();
  const patchStr = patch.toString();

  return majorStr + '.' + minorStr + '.' + patchStr;
}

/**
 * @param {number} code
 * @param {string} defaultStr
 * @returns {string}
 */
export function convertOptionalVersionCodeToStr(code, defaultStr) {
  if(isNumberSpecified(code)) {
    return convertVersionCodeToStr(code);
  }

  if(isStringSpecified(defaultStr)) {
    return defaultStr;
  }

  return '---';
}

/**
 * @param {?string} versionStr
 * @returns {?string}
 */
export function normalizeVersionStr(versionStr) {
  const version = parseVersion(versionStr);
  if(version === null) {
    return null;
  }
  return convertVersionToVersionStr(version);
}

/**
 * @typedef {Object} Version
 * @property {number} major
 * @property {number} minor
 * @property {number} patch
 */

/**
 * @param {?string} versionStr 
 * @returns {?Version}
 */
export function parseVersion(versionStr) {
  if(versionStr === undefined) {
    return null;
  }

  if(versionStr === null) {
    return null;
  }

  const parts = versionStr.split('.');
  if(parts.length < 2 || parts.length > 3) {
    return null;
  }

  const major = parseUint32(parts[0]);
  if(major === null) {
    return null;
  }

  const minor = parseUint32(parts[1]);
  if(minor === null) {
    return null;
  }

  let patch = 0;
  if(parts.length === 3) {
    patch = parseUint32(parts[2]);
    if(patch === null) {
      return null;
    }
  }

  return {
    major,
		minor,
		patch,
  };
}

/**
 * @param {?string} versionStr
 * @returns {VersionParsingReport}
 */
export function parseOptionalVersionInput(versionStr) {
  if(versionStr === undefined) {
    return new VersionParsingReport(
      null,
      new Error('Param versionStr is undefined.')
    );
  }

  if(versionStr === null) {
    return new VersionParsingReport(null, null);
  }

  if(typeof versionStr !== 'string') {
    return new VersionParsingReport(
      null,
      new Error('Param versionStr is not string.')
    );
  }

  const trimmedVersionStr = versionStr.trim();
  if(trimmedVersionStr.length === 0) {
    return new VersionParsingReport(null, null);
  }

  return parseVersion2(trimmedVersionStr);
}

/**
 * @param {?string} versionStr
 * @returns {VersionParsingReport}
 */
export function parseOptionalVersion(versionStr) {
  if(versionStr === null) {
    return new VersionParsingReport(null, null);
  }

  if(versionStr.length === 0) {
    return new VersionParsingReport(null, null);
  }

  return parseVersion2(versionStr);
}

/**
 * @param {?string} versionStr
 * @returns {VersionParsingReport}
 */
export function parseVersion2(versionStr) {
  if(versionStr === undefined) {
    return new VersionParsingReport(
      null,
      new Error('Param versionStr is undefined.')
    );
  }

  if(versionStr === null) {
    return new VersionParsingReport(
      null,
      new Error('Param versionStr is null.')
    );
  }

  if(typeof versionStr !== 'string') {
    return new VersionParsingReport(
      null,
      new Error('Param versionStr is not string.')
    );
  }

  if(versionStr.length === 0) {
    return new VersionParsingReport(
      null,
      new Error('Version string is empty.')
    );
  }

  const parts = versionStr.split('.');
  if(parts.length < 2 || parts.length > 3) {
    return new VersionParsingReport(
      null,
      new Error('Invalid format.')
    );
  }

  const major = parseUint32(parts[0]);
  if(major === null) {
    return new VersionParsingReport(
      null,
      new Error('Invalid format.')
    );
  }

  const minor = parseUint32(parts[1]);
  if(minor === null) {
    return new VersionParsingReport(
      null,
      new Error('Invalid format.')
    );
  }

  let patch = 0;
  if(parts.length === 3) {
    patch = parseUint32(parts[2]);
    if(patch === null) {
      return new VersionParsingReport(
        null,
        new Error('Invalid format.')
      );
    }
  }

  const version = {
    major,
    minor,
    patch,
  };

  return new VersionParsingReport(version, null);
}

/**
 * @param {?string} versionStr
 * @returns {?Error}
 */
export function validateMajorMinorVersionStr(versionStr) {
  const parsingReport = parseMajorMinorVersion(versionStr);
  if(parsingReport.error !== null) {
    return parsingReport.error;
  }
  return null;
}

/**
 * @param {?string} versionStr
 * @returns {VersionParsingReport}
 */
export function parseMajorMinorVersion(versionStr) {
  if(versionStr === undefined) {
    return new VersionParsingReport(
      null,
      new Error('Param versionStr is undefined.')
    );
  }

  if(versionStr === null) {
    return new VersionParsingReport(
      null,
      new Error('Param versionStr is null.')
    );
  }

  if(typeof versionStr !== 'string') {
    return new VersionParsingReport(
      null,
      new Error('Param versionStr is not string.')
    );
  }

  if(versionStr.length === 0) {
    return new VersionParsingReport(
      null,
      new Error('Version string is empty.')
    );
  }

  const parts = versionStr.split('.');
  if(parts.length !== 2) {
    return new VersionParsingReport(
      null,
      new VersionMajorMinorInvalidFormatError('Invalid format. Valid format is "Major.Minor".')
    );
  }

  const major = parseUint32(parts[0]);
  if(major === null) {
    return new VersionParsingReport(
      null,
      new Error('Invalid format.')
    );
  }

  const minor = parseUint32(parts[1]);
  if(minor === null) {
    return new VersionParsingReport(
      null,
      new Error('Invalid format.')
    );
  }

  const version = {
    major: major,
    minor: minor,
    patch: 0,
  };

  return new VersionParsingReport(version, null);
}

class VersionParsingReport {
  /**
   * @param {?Version} version
   * @param {?Error} error
   */
  constructor(version, error) {
    /** @type {?Version} */
    this.version = version;

    /** @type {?Error} */
    this.error = error;
  }
}

export class VersionMajorMinorInvalidFormatError extends Error {
  /**
   * @param {string} message
   */
  constructor(message) {
    super(message);
    this.name = 'VersionMajorMinorInvalidFormatError';
  }
}

/**
 * @param {Version} version
 * @returns {string}
 */
export function convertVersionToVersionStr(version) {
  return version.major.toString() + '.' + version.minor.toString() + '.' + version.patch.toString();
}

//-----------------------------------------------------------------------------

/**
 * @param {?string} str
 * @returns {Object}
 */
export function parseOptionalUint32Input(str) {
  if((str === undefined) || (str === null)) {
    return {
      n: null,
      error: null,
    };
  }

  const trimmedStr = str.trim();
  if(trimmedStr.length === 0) {
    return {
      n: null,
      error: null,
    };
  }

  if(!/^\d+$/.test(trimmedStr)) {
    return {
      n: null,
      error: new Error(`String '${trimmedStr}' is not valid uint32 string.`),
    };
  }

  const maxN = 4294967295;  // 2^32 - 1

  const maxNStrLen = maxN.toString().length;
  if(trimmedStr.length > maxNStrLen) {
    return {
      n: null,
      error: new Error(`String '${trimmedStr}' is longer than max uint32 str.`),
    };
  }

  const n = Number.parseInt(trimmedStr, 10);
  if(isNaN(n)) {
    return {
      n: null,
      error: new Error(`String '${trimmedStr}' is not valid number.`),
    };
  }

  if(n < 0) {
    return {
      n: null,
      error: new Error(`Number '${n}' < 0.`),
    };
  }

  if(n > maxN) {
    return {
      n: null,
      error: new Error(`Number '${n}' > max uint32 value (${maxN}).`),
    };
  }

  return {
    n: n,
    error: null,
  };
}

/**
 * @param {string} str 
 * @returns {?number}
 */
export function parseUint32(str) {
  if(str === undefined) {
    return null;
  }

  if(str === null) {
    return null;
  }

  const trimmedStr = str.trim();
  if(trimmedStr.length === 0) {
    return null;
  }

  if(!/^\d+$/.test(trimmedStr)) {
    return null;
  }

  const maxN = 4294967295;  // 2^32 - 1

  const maxNStrLen = maxN.toString().length;
  if(trimmedStr.length > maxNStrLen) {
    return null;
  }

  const n = Number.parseInt(trimmedStr, 10);
  if(isNaN(n)) {
    return null;
  }

  if(n < 0) {
    return null;
  }

  if(n > maxN) {
    return null;
  }

  return n;
}

/**
 * @param {string} str
 * @returns {string}
 */
export function escapeHtml(str) {
  let result = str;
  result = result.replaceAll('&', '&amp;');
  result = result.replaceAll('<', '&lt;');
  result = result.replaceAll('>', '&gt;');
  result = result.replaceAll('"', '&quot;');
  result = result.replaceAll("'", '&#039;');
  return result;
}

//-----------------------------------------------------------------------------

export function isLocalOrStage() {
  return IS_DEV;
}

export function isStageOrProd() {
  return process.env.NODE_ENV === 'production';
}

export function isLocal() {
  return isLocalOrStage() && !isStageOrProd();
}

export function isProd() {
  return !isLocalOrStage() && isStageOrProd();
}

export function isLocalOrProd() {
  return isLocal() || isProd();
}

//-----------------------------------------------------------------------------

/**
 * Sort numbers in place.
 * @param {number[]} numbers 
 */
export function sortNumbers(numbers) {
  numbers.sort(compareNumbers);
}

/**
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
function compareNumbers(a, b) {
  return a - b;
}

/**
 * @param {number[]} numbers
 * @returns {number[]}
 */
export function getSortedNumbers(numbers) {
  // Do not change original array. Sort a copy.
  const copy = numbers.slice();
  sortNumbers(copy);
  return copy;
}

/**
 * @param {string} str
 * @param {string} substr
 * @returns {string}
 */
export function getStringWithHighlightedSubstringCaseInsensitive(str, substr) {
  if(!isStringSpecified(substr)) {
    return str;
  }

  const re = new RegExp(substr, 'gi');
  return str.replace(re, `<strong>$&</strong>`);
}

/**
 * @param {Object[]} items 
 */
export function addKeys(items) {
  if(items === undefined) {
    return;
  }

  if(items === null) {
    return;
  }

  for(let i = 0; i < items.length; i++) {
    items[i].key = i;
  }
}

export function addParamToQueryObject(queryObject, paramName, paramValue) {
  if(paramValue !== null) {
    queryObject[paramName] = paramValue;
  }
}

export function convertOptionalValueToQueryParam(v) {
  if(v === null) {
    return undefined;
  }
  return v;
}

export function convertOptionalValueToOptionalString(v) {
  if(v === null) {
    return null;
  }
  return v.toString();
}

/**
 * @param {number} v
 * @returns {?number}
 */
export function zeroToNull(v) {
  if(v === 0) {
    return null;
  }
  return v;
}

//-----------------------------------------------------------------------------

/**
 * Insert space between groups by 3 digits.
 * @param {number} n 
 * @returns {string}
 */
export function getFormattedNumber(n) {
  const GROUP_LENGTH = 3;
  const SEPARATOR = ' ';

  const original = n.toString();
  let result = '';
  
  let groupIndex = 0;
  for(;;) {
    const groupStart = original.length - (groupIndex+1)*GROUP_LENGTH;
    const groupEnd   = original.length - (groupIndex)*GROUP_LENGTH;
    const group = original.substring(groupStart, groupEnd);

    if(result.length === 0) {
      result = group;
    } else {
      result = group + SEPARATOR + result;
    }

    if(groupStart <= 0) {
      return result;
    }
    
    groupIndex++;
  }
}

/**
 * @param {number} d 
 * @returns {string}
 */
export function convertDifferenceToOptionalStr(d) {
  if(d === 0) {
    return '';
  }
  return getFormattedDifference(d);
}

/**
 * @param {number} d 
 * @returns {string}
 */
 export function getFormattedDifference(d) {
  const signStr = getSignStr(d);

  const n = Math.abs(d);
  const formattedNumber = getFormattedNumber(n);

  return signStr + formattedNumber;
}

/**
 * @param {number} n 
 * @returns {string}
 */
function getSignStr(n) {
  if(n > 0) {
    return '+';
  }
  if(n < 0) {
    return '-';
  }
  return '';
}

//-----------------------------------------------------------------------------

export function hasRouteByName(routes, name) {
  for(const route of routes) {
    if(route.name === name) {
      return true;
    }
  }

  return false;
}

//-----------------------------------------------------------------------------

export function getAudienceOptions() {
  const options = [];

  const audienceIds = getEnumM3SeqABTestAudienceTagValues();
  for(const audienceId of audienceIds) {
    const option = {
      value: audienceId,
      text:  getAudienceName(audienceId),
    };
    options.push(option);
  }

  return options;
}

/**
 * @param {number} audienceId 
 * @returns {string}
 */
export function getAudienceName(audienceId) {
  if(audienceId === null || audienceId === 0) {
    return 'all';
  }
  return getEnumM3SeqABTestAudienceTagName(audienceId);
}

//-----------------------------------------------------------------------------

/**
 * @param {string} str
 * @returns {number[]}
 */
export function convertCSVStringToNumbers(str) {
  const parts = str.split(',');

  const nums = [];
  for(const part of parts) {
    const num = parseInt(part, 10);
    nums.push(num);
  }

  return nums;
}

//-----------------------------------------------------------------------------

/**
 * @param {number} h
 * @returns {number}
 */
export function convertHourToSec(h) {
  return h * 3600;
}

/**
 * @param {Date} m
 * @returns {Date}
 */
export function floorMomentToSec(m) {
  const k = 1000 * 60;
  return new Date(Math.floor(m.getTime() / k) * k);
}

/**
 * @param {Date} m
 * @returns {Date}
 */
 export function floorMomentTo5Min(m) {
  const k = 1000 * 60 * 5;
  return new Date(Math.floor(m.getTime() / k) * k);
}

//-----------------------------------------------------------------------------

/**
 * @param {boolean} v
 * @returns {string}
 */
export function getBoolStr(v) {
  return v ? 'Yes' : 'No';
}

export function createShortExtIdMap(extIds) {
  // 3 символа для кода платформы
  // 1 симовл для разделителя
  // 4 символа для части данных.
  //
  // Пример
  // and:ZJ7j49ngPR856nP8AHS4Flo4AoKLGJ0/yqo/L2Px70Q=
  // Результат
  // and:ZJ7j
  const minPrefixLen = 8;

  return createUniqPrefixMap(extIds, minPrefixLen);
}

function createUniqPrefixMap(strs, minPrefixLen) {
  const maxLen = getMaxStingLen(strs);

  for(let prefixLen = minPrefixLen; prefixLen < maxLen; prefixLen++) {
    const m = tryCreateUniqPrefixMap(strs, prefixLen);
    if(m !== null) {
      return m;
    }
  }

  throw new Error(`Can not create uniq prefix map. Input strings are no uniq. Strs: ${strs}.`);
}

function getMaxStingLen(strs) {
  let maxLen = 0;

  for(const str of strs) {
    if(str.length > maxLen) {
      maxLen = str.length;
    }
  }

  return maxLen;
}

function tryCreateUniqPrefixMap(strs, prefixLen) {
  const m = new Map();

  for(const str of strs) {
    const prefix = str.substring(0, prefixLen);
    if(mapHasValue(m, prefix)) {
      return null; // No success (failed). Prefix len is too short.
    }
    m.set(str, prefix);
  }

  return m;
}

export function mapHasValue(m, v) {
  for(const storedValue of m.values()) {
    if(storedValue === v) {
      return true;
    }
  }
  return false;
}

export function containSpace(str) {
  return /\s/.test(str);
}
